import type { PreferencesData } from 'src/api/procurer';
import type { ContractPreferencesCurrentStep as CurrentStep } from 'src/types';

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { usePreferencesStore } from 'src/store';
import { getPreferences, savePreferences } from 'src/api/procurer';

import { useSetupNavigation } from './useSetupNavigation';

export const useSaveProcurerPreferences = () => {
  const {
    geography,
    themes,
    impact,
    scoring,
    qualitative,
    finalizing,
    completedSteps,
    mode,
    setError,
  } = usePreferencesStore();
  const queryClient = useQueryClient();
  const { navigateToNextStep } = useSetupNavigation(mode);

  const savePreferencesMutation = useMutation({
    mutationFn: savePreferences,
  });

  const savePreferencesMutateFn = async (
    currentStep: CurrentStep,
    overrideData?: PreferencesData,
    overrideSetError?: (path: string, errorObj: any) => void,
    skipNavigation = false
  ) => {
    setError({});
    const updatedCompletedSteps = [...new Set([...completedSteps, currentStep])];
    try {
      await savePreferencesMutation.mutateAsync({
        currentStep,
        geography: {
          geographicalAreas: geography?.geographicalAreas ?? [],
          priorityGeographicalAreas: geography?.priorityGeographicalAreas ?? [],
        },
        themes,
        impact,
        scoring,
        qualitative,
        finalizing: finalizing ?? undefined,
        completedSteps: updatedCompletedSteps,
        ...overrideData,
      });
      queryClient.invalidateQueries({ queryKey: ['procurer-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['user', 'my-info'] });
      if (!skipNavigation) {
        navigateToNextStep(currentStep);
      }
    } catch (error: any) {
      if (error.data?.issues) {
        if (overrideSetError) {
          error.data.issues.forEach((issue: any) => {
            overrideSetError(issue.path[1], { message: issue.message });
          });
        } else {
          const errorsObj: Record<string, any> = {};
          error.data.issues.forEach((issue: any) => {
            if (!errorsObj[issue.path[0]]) {
              errorsObj[issue.path[0]] = {};
            }
            if (issue.path[1]) {
              errorsObj[issue.path[0]][issue.path[1]] = { message: issue.message };
            } else {
              errorsObj[issue.path[0]] = { message: issue.message };
            }
          });
          setError(errorsObj);
        }
      }
      throw error;
    }
  };

  return { savePreferencesMutateFn, isPending: savePreferencesMutation.isPending };
};

export const useLoadProcurerPreferences = ({ enabled }: { enabled: boolean }) => {
  const { data } = useQuery({
    queryKey: ['procurer-preferences'],
    queryFn: () => getPreferences(),
    enabled,
  });
  const {
    setMode,
    setGeography,
    setThemes,
    setImpact,
    setScoring,
    setQualitative,
    setFinalizing,
    setCompletedSteps,
    incrementHydration,
  } = usePreferencesStore.getState();

  useEffect(() => {
    if (data && enabled) {
      setMode('preferences');
      if (data.completedSteps) {
        setCompletedSteps(data.completedSteps);
      }
      if (data.geography) {
        const geographyData = {
          ...data.geography,
          geographicalAreas: data.geography.geographicalAreas ?? [],
        };
        setGeography(geographyData);
      }
      if (data.themes) {
        setThemes(data.themes);
      }
      if (data.impact) {
        setImpact(data.impact);
      }
      if (data.scoring) {
        setScoring(data.scoring);
      }

      if (data.qualitative) {
        setQualitative(data.qualitative);
      }

      if (data.finalizing) {
        setFinalizing(data.finalizing);
      }
      incrementHydration();
    }
  }, [
    data,
    enabled,
    setGeography,
    setThemes,
    setImpact,
    setFinalizing,
    setScoring,
    setQualitative,
    setCompletedSteps,
    incrementHydration,
    setMode,
  ]);
};
